import React from 'react';
import { AppResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * RegisterBrandingPanel — painel de MARCA fixo (coluna esquerda) da tela de Cadastro.
 *
 * Anatomia do mockup auth-register.html: painel flat na cor primária (white-label),
 * logo/nome no topo, headline + features no meio, rodapé embaixo (justify-between).
 * Fixo à esquerda no desktop (NÃO rola); some no mobile (vira m-logo compacto no form).
 *
 * NÃO edita o AuthBrandingPanel compartilhado (usado por Login/Reset) — componente
 * próprio desta tela, importado por caminho relativo direto.
 */

const FEATURES: { icon: IconName; text: string }[] = [
  { icon: 'message-circle', text: 'Multicanal em uma caixa de entrada' },
  { icon: 'zap', text: 'Agentes de IA que atendem por você' },
  { icon: 'trending-up', text: 'CRM e funis com automações' },
];

interface RegisterBrandingPanelProps {
  app: AppResponse | null;
  primaryColor: string;
}

export const RegisterBrandingPanel: React.FC<RegisterBrandingPanelProps> = ({ app, primaryColor }) => {
  const brandName = app?.name || 'TROIA';

  return (
    <aside
      className="hidden min-[860px]:flex min-[860px]:w-1/2 min-[860px]:h-full relative overflow-hidden flex-col justify-between px-12 py-12 text-white"
      style={{ backgroundColor: primaryColor }}
      data-testid="register-brand-panel"
    >
      {/* Círculos decorativos */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{ right: -80, bottom: -80, width: 320, height: 320, backgroundColor: 'rgba(255,255,255,0.06)' }}
      />
      <div
        className="absolute rounded-full pointer-events-none"
        style={{ left: -60, top: 60, width: 180, height: 180, backgroundColor: 'rgba(255,255,255,0.05)' }}
      />

      {/* Topo — logo + nome (white-label) */}
      <div className="relative z-10 flex items-center gap-3 font-extrabold text-xl tracking-tight">
        {app?.logo ? (
          <img src={app.logo} alt={brandName} className="h-10 w-auto" />
        ) : (
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-black"
            style={{ backgroundColor: '#ffffff', color: primaryColor }}
          >
            {brandName.charAt(0).toUpperCase()}
          </span>
        )}
        {brandName}
      </div>

      {/* Meio — headline + subtítulo + features */}
      <div className="relative z-10 max-w-md">
        <h2 className="text-3xl font-bold tracking-tight leading-tight mb-3.5">
          Atendimento, CRM e IA em um só lugar.
        </h2>
        <p className="text-[15px] leading-relaxed mb-7" style={{ color: 'rgba(255,255,255,0.85)' }}>
          Centralize WhatsApp, Instagram e e-mail, automatize com agentes de IA e feche mais negócios.
        </p>
        <ul className="flex flex-col gap-3.5">
          {FEATURES.map((feature) => (
            <li key={feature.text} className="flex items-center gap-3 text-[14.5px]" style={{ color: 'rgba(255,255,255,0.94)' }}>
              <span
                className="w-[26px] h-[26px] rounded-lg flex items-center justify-center flex-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.16)' }}
              >
                <Icon name={feature.icon} className="w-4 h-4 text-white" />
              </span>
              {feature.text}
            </li>
          ))}
        </ul>
      </div>

      {/* Rodapé */}
      <div className="relative z-10 text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
        © {new Date().getFullYear()} {brandName} · Todos os direitos reservados
      </div>
    </aside>
  );
};

RegisterBrandingPanel.displayName = 'RegisterBrandingPanel';
